/**
 * Three Dashboard widgets surfacing community + per-user signals:
 *
 *   1. LuckyBreedersHall    — last N Lucky breeds across the community
 *   2. LegendaryPalsShowcase — user-owned Legendaries with passives
 *   3. LuckyPalLeaderboard  — lifetime Lucky-pal count, top 10
 *
 * All three are public-data widgets except (2), which is per-user and
 * silently no-ops on the env-credential admin (no DB row → empty box).
 * Each widget gracefully returns null while loading + returns an
 * empty-state card when there's no data yet.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PalPortrait } from "@/components/PalAssets";
import { ElementChip } from "@/components/PalAssets";
import { Sparkles, Trophy, Crown } from "lucide-react";

// ── Lucky Breeders Hall ────────────────────────────────────────────
export function LuckyBreedersHall() {
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = () => api.get("/palgame/breed/recent-lucky?limit=5")
      .then((r) => { if (active) setRecent(r.data.recent || []); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    load();
    const id = setInterval(load, 30000); // refresh every 30s
    return () => { active = false; clearInterval(id); };
  }, []);

  if (loading) return null;

  return (
    <section
      className="mt-8 fade-up"
      style={{ animationDelay: "0.25s" }}
      data-testid="lucky-breeders-hall"
    >
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-xl flex items-center gap-2" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
            <Sparkles className="text-yellow-300" size={18} /> Lucky Breeders Hall
          </h3>
          <p className="text-xs text-[var(--muted)] mt-1">
            The community's rarest breeds — the 1-in-256 rolls.
          </p>
        </div>
        <Link to="/breeding" className="text-xs text-[var(--teal)] glow-teal" data-testid="lucky-breeders-hall-link">
          Try breeding →
        </Link>
      </div>

      {recent.length === 0 ? (
        <div className="glass-card p-6 text-center text-sm text-[var(--muted)]">
          No Lucky breeds yet — be the first to roll the 1-in-256.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {recent.map((b, i) => (
            <Link
              key={`${b.at}-${i}`}
              to={`/paldex/${encodeURIComponent((b.result || "").toLowerCase())}`}
              className="glass-card p-4 hover:border-yellow-400/60 transition-colors"
              data-testid={`lucky-breed-${i}`}
              style={{ borderColor: "rgba(250, 204, 21, 0.35)" }}
            >
              <div className="flex items-center gap-3">
                <div className="relative shrink-0">
                  <PalPortrait name={b.result} size="w-14 h-14" />
                  <span
                    className="absolute -top-1 -right-1 text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-900/80 border border-yellow-400/60 text-yellow-200 uppercase tracking-wider"
                    data-testid={`lucky-breed-${i}-badge`}
                  >
                    ✨
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[var(--bone)] truncate font-medium">{b.result}</div>
                  <div className="text-[10px] text-[var(--muted)] truncate">
                    {b.user_name} bred {b.parent1} × {b.parent2}
                  </div>
                  <div className="text-[10px] text-yellow-300/80 mt-0.5 uppercase tracking-wider">
                    Lucky offspring
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

// ── My Legendary Pals ──────────────────────────────────────────────
export function LegendaryPalsShowcase() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/palgame/legendary-pals")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;
  const owned = data.owned || [];
  const totalSpecies = data.total_legendary_species || 0;

  return (
    <section
      className="mt-8 fade-up"
      style={{ animationDelay: "0.3s" }}
      data-testid="legendary-pals-showcase"
    >
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-xl flex items-center gap-2" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
            <Crown className="text-yellow-300" size={18} /> My Legendary Pals
          </h3>
          <p className="text-xs text-[var(--muted)] mt-1">
            {owned.length === 0
              ? `0 / ${totalSpecies} caught — track down the apex pals to fill the hall.`
              : `${owned.length} / ${totalSpecies} caught — your rarest captures.`}
          </p>
        </div>
        <Link to="/palgame" className="text-xs text-[var(--teal)] glow-teal" data-testid="legendary-pals-showcase-link">
          View Pal Box →
        </Link>
      </div>

      {owned.length === 0 ? (
        <div className="glass-card p-6 text-center text-sm text-[var(--muted)]">
          Catch a Legendary (Jetragon, Frostallion, Paladius, Necromus, Frostallion Noct, Neptilius)
          to unlock this hall.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {owned.map((p) => (
            <div
              key={p.pal_name}
              className="glass-card p-4 relative"
              data-testid={`legendary-pal-${p.pal_name.replace(/\s/g, "-")}`}
              style={{
                borderColor: p.is_lucky ? "rgba(250, 204, 21, 0.5)" : "rgba(155, 89, 182, 0.4)",
              }}
            >
              <div className="flex items-start gap-3">
                <div className="relative shrink-0">
                  <PalPortrait name={p.pal_name} size="w-16 h-16" />
                  {p.is_lucky && (
                    <span className="absolute -top-1 -right-1 text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-900/80 border border-yellow-400/60 text-yellow-200 uppercase tracking-wider">
                      ✨
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <div className="text-sm text-[var(--bone)] truncate font-medium">{p.pal_name}</div>
                    <div className="text-[10px] text-[var(--teal)] glow-teal shrink-0">Lv {p.level}</div>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(p.elements || []).map((e) => (
                      <ElementChip key={e} element={e} size={10} showLabel={false} />
                    ))}
                  </div>
                  {p.passives.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {p.passives.map((nm) => (
                        <span
                          key={nm}
                          className="text-[9px] px-1.5 py-0.5 rounded border border-yellow-400/30 bg-yellow-900/20 text-yellow-200 uppercase tracking-wider"
                          data-testid={`legendary-pal-${p.pal_name.replace(/\s/g, "-")}-passive-${nm.replace(/\s/g, "-")}`}
                        >
                          {nm}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ── Lucky Pal Leaderboard ──────────────────────────────────────────
export function LuckyPalLeaderboard() {
  const [entries, setEntries] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/palgame/leaderboard/lucky?limit=10")
      .then((r) => {
        setEntries(r.data.entries || []);
        setMe(r.data.me || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  // Derive a short motivational hint for the "Your Rank" pill so the
  // user knows what to chase. The top of the visible board is at index 0.
  const top10Threshold = entries.length === 10 ? entries[9].lucky_count : null;
  let chaseHint = null;
  if (me && top10Threshold != null) {
    const needed = top10Threshold - me.lucky_count + 1;
    if (needed > 0) {
      chaseHint = `Catch ${needed} more ✨ to break into the top 10.`;
    }
  } else if (me && me.lucky_count === 0) {
    chaseHint = "Catch your first ✨ to get on the board.";
  }

  // Hide the "Your Rank" pill if the user is already inside the visible
  // top 10 — they can see their row directly, no point duplicating.
  const meInTopN = me && entries.some((e) => e.user_id === me.user_id);
  const showMePill = me && !meInTopN;

  return (
    <section
      className="mt-8 fade-up"
      style={{ animationDelay: "0.35s" }}
      data-testid="lucky-pal-leaderboard"
    >
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-xl flex items-center gap-2" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
            <Trophy className="text-yellow-300" size={18} /> Lucky Pal Leaderboard
          </h3>
          <p className="text-xs text-[var(--muted)] mt-1">
            Lifetime ✨ Lucky-pal count. Catch one, keep it, brag forever.
          </p>
        </div>
      </div>

      {showMePill && (
        <div
          className="glass-card p-3 mb-3 flex items-center gap-3 border-yellow-400/30"
          data-testid="lucky-leaderboard-me-pill"
          style={{ borderColor: "rgba(250, 204, 21, 0.30)" }}
        >
          <div className="shrink-0 w-7 h-7 rounded-full border border-yellow-400/40 bg-yellow-500/15 text-yellow-200 flex items-center justify-center text-[11px] font-mono">
            {me.rank ? `#${me.rank}` : "—"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-[var(--bone)] truncate">
              <span className="text-[var(--muted)] text-xs uppercase tracking-wider mr-2">You</span>
              {/* Hide the duplicate "You" fallback that comes back when */}
              {/* the env-admin (no user_name) hits this endpoint. */}
              {me.user_name && me.user_name !== "You" ? me.user_name : null}
            </div>
            {chaseHint && (
              <div className="text-[10px] text-[var(--muted)] mt-0.5">
                {chaseHint}
              </div>
            )}
          </div>
          <div
            className="text-sm font-semibold text-yellow-300 glow-gold inline-flex items-center gap-1 shrink-0"
            data-testid="lucky-leaderboard-me-count"
          >
            ✨ {me.lucky_count}
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <div className="glass-card p-6 text-center text-sm text-[var(--muted)]">
          No Lucky pals caught yet — the 1-in-512 chase is wide open.
        </div>
      ) : (
        <div className="glass-card p-4">
          <ol className="space-y-2" data-testid="lucky-pal-leaderboard-list">
            {entries.map((e) => (
              <li
                key={e.user_id}
                className={`flex items-center gap-3 px-3 py-2 rounded border ${rowStyleByRank(e.rank)}`}
                data-testid={`lucky-pal-leaderboard-row-${e.rank}`}
              >
                <RankBadge rank={e.rank} />
                <div className="flex-1 truncate text-sm text-[var(--bone)]">{e.user_name}</div>
                <div className="text-sm font-semibold text-yellow-300 glow-gold inline-flex items-center gap-1">
                  ✨ {e.lucky_count}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}

// Podium tint for the top 3 — subtle background + matching border so the
// gold / silver / bronze placements pop without overwhelming the row.
function rowStyleByRank(rank) {
  if (rank === 1) return "bg-yellow-500/10 border-yellow-400/40";
  if (rank === 2) return "bg-slate-300/8 border-slate-300/30";
  if (rank === 3) return "bg-amber-700/12 border-amber-600/30";
  return "bg-black/20 border-white/5";
}

function RankBadge({ rank }) {
  const styleByRank = {
    1: "bg-yellow-500/20 border-yellow-400/60 text-yellow-200",
    2: "bg-slate-300/15 border-slate-300/50 text-slate-200",
    3: "bg-amber-700/25 border-amber-600/50 text-amber-200",
  };
  const style = styleByRank[rank] || "bg-white/5 border-white/15 text-[var(--muted)]";
  return (
    <div
      className={`shrink-0 w-7 h-7 rounded-full border flex items-center justify-center text-[11px] font-mono ${style}`}
    >
      #{rank}
    </div>
  );
}
